import { BonusFlipItemProps } from '..';
import { ComponentType, FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { useFormatBonusTerms } from './hooks';
import { GameV1 } from '@/api/games';
import { ScrollBlock } from '@/components/ScrollBlock';

export const BonusTerms: FC<BonusFlipItemProps & { bonusGames?: GameV1[] }> = ({
  bonus,
  bonusGames,
}) => {
  const { t } = useTranslation('bonuses');
  const { bonusTerms } = useTheme();
  const {
    dateReceived,
    activateUntilDate,
    expireDate,
    wager,
    amountWager,
    wagerRequirement,
    wageredPercent,
    bonusAmount,
    bonusFreespins,
    bonusGamesLinks,
    status,
  } = useFormatBonusTerms(bonus, bonusGames);

  const TermsPoint: FC<{
    translationKey: string;
    value: string | number;
    icon: ComponentType<{}>;
  }> = useCallback(
    ({ translationKey, value, icon: Icon }) => {
      return (
        <li>
          <Icon />
          <span className="bonus-terms">
            {t(`bonusTerms.${translationKey}`, { value })}
          </span>
        </li>
      );
    },
    [t]
  );

  return (
    <ScrollBlock height="100%" width="100%" scrollTopMargin={0}>
      <ul>
        <TermsPoint
          translationKey="dateReceived"
          value={dateReceived}
          icon={bonusTerms.frequency.component}
        />
        {activateUntilDate && (
          <TermsPoint
            translationKey="activateUntil"
            value={activateUntilDate}
            icon={bonusTerms.frequency.component}
          />
        )}
        {expireDate && (
          <TermsPoint
            translationKey="expireDate"
            value={expireDate}
            icon={bonusTerms.hourglass.component}
          />
        )}
        {wager && (
          <TermsPoint
            translationKey="wager"
            value={wager}
            icon={bonusTerms.wager.component}
          />
        )}
        {amountWager && (
          <TermsPoint
            translationKey="amountWager"
            value={amountWager}
            icon={bonusTerms.maxWinFs.component}
          />
        )}
        {wagerRequirement && (
          <TermsPoint
            translationKey="wagerRequirement"
            value={wagerRequirement}
            icon={bonusTerms.maxWinFs.component}
          />
        )}
        {wageredPercent && (
          <TermsPoint
            translationKey="wagerPercent"
            value={wageredPercent}
            icon={bonusTerms.rtp.component}
          />
        )}
        {bonusAmount && (
          <TermsPoint
            translationKey="bonusAmount"
            value={bonusAmount}
            icon={bonusTerms.maxBonus.component}
          />
        )}

        <TermsPoint
          translationKey="status"
          value={status}
          icon={bonusTerms.info.component}
        />

        {bonusGames && (
          <li>
            <bonusTerms.game.component />
            {t('bonusTerms.bonusGame')} {bonusGamesLinks}
          </li>
        )}

        {bonusFreespins && (
          <TermsPoint
            translationKey="freespinsAwarded"
            value={bonusFreespins}
            icon={bonusTerms.fsAmount.component}
          />
        )}

        <TermsPoint
          translationKey="currency"
          value={bonus.currency}
          icon={bonusTerms.maxCashout.component}
        />
      </ul>
    </ScrollBlock>
  );
};
